import { createUseStyles } from "react-jss";
import { NavLink, Outlet } from "react-router-dom";
import { chooseLanguage } from "../utils/chooseLanguage";
import extractStrokesFromSVG, {
  InterpretationError,
} from "../utils/extractStrokesFromSVG";
import { getSymbol } from "../utils/getSymbol";

interface Translation {
  letter: string;
  symbol: string;
}

export default function CharacterView({
  language,
  currentRoute,
  isEnglishLetter,
  translationArray,
}: {
  language: any;
  currentRoute: string;
  isEnglishLetter: boolean;
  translationArray: Translation[];
}) {
  const classes = useStyles();

  return (
    <div className={classes.wrapper}>
      <nav className={classes.nav}>
        {Object.keys(language).map((character) => {
          const characterStatus = getCharacterStatus(
            character as keyof typeof language,
            currentRoute as string
          );

          return characterStatus === "missing" ? (
            <div className={`${classes.link} ${classes.disabledLink}`}>
              {character}
            </div>
          ) : (
            <NavLink
              key={character}
              to={`character/${encodeURIComponent(character)}`}
              className={({ isActive }) =>
                [
                  classes.link,
                  isActive ? classes.activeLink : "",
                  characterStatus === "failing" ? classes.failingLink : "",
                  " character-button",
                ].join(" ")
              }
              state={currentRoute}
            >
              {isEnglishLetter
                ? character.replace(/_/g, " ")
                : getSymbol(character, translationArray)}
            </NavLink>
          );
        })}
      </nav>
      <main style={{ marginTop: "20px" }}>
        <Outlet />
      </main>
    </div>
  );
}

type CharacterStatus = "working" | "failing" | "missing";

function getCharacterStatus(
  character: any,
  currentRoute: any
): CharacterStatus {
  const language = chooseLanguage(currentRoute);
  try {
    //@ts-ignore
    extractStrokesFromSVG(language[character]);
    return "working";
  } catch (error) {
    if (error instanceof InterpretationError && error.isNotDoneYet) {
      return "missing";
    } else {
      return "failing";
    }
  }
}

const useStyles = createUseStyles({
  wrapper: {
    display: "flex",
    flexDirection: "column",
  },
  nav: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    width: "100%",
    gap: "0.5rem",
  },
  link: {
    "fontSize": "2rem",
    "textDecoration": "none",
    "borderRadius": "0.5rem",
    "width": "3rem",
    "height": "3rem",
    "lineHeight": "3rem",
    "textAlign": "center",
    "background": "#eee",
    "border": "1px solid rgba(0, 0, 0, 0.1)",
    "color": "black",
    "row": "span 2", // add the row property here
    "&:hover:not($activeLink):not($disabledLink)": {
      background: "#ddd",
    },
  },
  row: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
  },
  character: {
    "display": "flex",
    "flexDirection": "column",
    "justifyContent": "center",
    "alignItems": "center",
    "width": "3rem",
    "height": "3rem",
    "margin": "0.5rem",
    "background": "#eee",
    "border": "1px solid rgba(0, 0, 0, 0.1)",
    "color": "black",
    "&:hover:not($activeLink):not($disabledLink)": {
      background: "#ddd",
    },
  },
  activeLink: {
    background: "#333",
    borderColor: "#333",
    color: "white",
  },
  disabledLink: {
    cursor: "default",
    opacity: 0.25,
    background: "#ddd",
    borderColor: "#ddd",
  },
  failingLink: {
    borderColor: "red",
    outline: "1px solid red",
  },
});
